import React from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface FieldsetCardProps {
  /**
   * The content to display inside the card
   */
  children: React.ReactNode;
  /**
   * Whether the form has unsaved changes - controls padding and save button visibility
   */
  isDirty?: boolean;
  /**
   * Whether the save button is in loading state
   */
  isLoading?: boolean;
  /**
   * Whether the save button is disabled
   */
  disabled?: boolean;
  /**
   * The label for the save button
   */
  saveButtonLabel?: string;
  /**
   * Whether to show the save button - if undefined, will show when isDirty is true
   */
  showSaveButton?: boolean;
  /**
   * The form ID this card belongs to - if provided, button will be type="submit"
   */
  formId?: string;
  /**
   * Custom onClick handler for save button - if provided, button will be type="button"
   */
  onSave?: () => void;
  /**
   * Additional CSS classes to apply to the card
   */
  className?: string;
}

export const FieldsetCard: React.FC<FieldsetCardProps> = ({
  children,
  isDirty = false,
  isLoading = false,
  disabled = false,
  saveButtonLabel,
  showSaveButton,
  formId,
  onSave,
  className,
}) => {
  // Determine if save button should be rendered (keep in DOM for transitions)
  const shouldRenderSaveButton = showSaveButton ?? !!saveButtonLabel;

  return (
    <div
      className={cn(
        'relative rounded-lg border border-border bg-background p-6 transition-[padding] duration-300 ease-in-out',
        shouldRenderSaveButton && isDirty ? 'pb-23 delay-0' : 'pb-7 delay-600',
        className
      )}
    >
      {children}

      {/* Animated Save Button */}
      {shouldRenderSaveButton && (
        <div className="flex justify-end">
          <Button
            type={formId ? 'submit' : 'button'}
            form={formId}
            isLoading={isLoading}
            disabled={disabled}
            onClick={onSave}
            className={cn(
              'absolute translate-y-6 transition-[opacity,visibility] duration-300',
              isDirty
                ? 'visible opacity-100 delay-300 ease-in-out'
                : 'invisible opacity-0 delay-200'
            )}
          >
            {saveButtonLabel}
          </Button>
        </div>
      )}
    </div>
  );
};
